import { CheckCircle, XCircle, CircleAlert } from "lucide-react";

import { formatNumber } from "@/utils/numberFormat";

import styles from "./OAValorizationCell.module.scss";

const PERSISTED_PRICE_STATE_IDS = [2, 4, 5];

const resolveEffectivePrice = (detail, usesPersistedPrice) => (usesPersistedPrice ? detail.price_local_oa : detail.price_local);

const computeValorizationSummary = (details, stateId) => {
	const usesPersistedPrice = PERSISTED_PRICE_STATE_IDS.includes(stateId);

	let computedTotal = 0;
	let unvaluedCount = 0;

	const productRows = details.map((detail) => {
		const effectivePrice = resolveEffectivePrice(detail, usesPersistedPrice);
		const priceNumber = Number(effectivePrice);
		const quantityNumber = Number(String(detail.quantity ?? 0).replace(",", "."));
		const isUnvalued = !effectivePrice || priceNumber === 0 || Number.isNaN(priceNumber);

		if (isUnvalued) {
			unvaluedCount += 1;
		} else {
			computedTotal += priceNumber * quantityNumber;
		}

		return {
			productName: detail.product_name || "-",
			effectivePrice: isUnvalued ? null : priceNumber,
			quantity: quantityNumber,
			subtotal: isUnvalued ? 0 : priceNumber * quantityNumber,
			isUnvalued,
		};
	});

	const valuedCount = details.length - unvaluedCount;
	return { computedTotal, unvaluedCount, valuedCount, productRows, totalProducts: details.length };
};

const resolveValorizationStatus = ({ totalProducts, unvaluedCount, valuedCount }) => {
	if (totalProducts === 0) return { type: "empty", Icon: null, label: "Sin productos" };
	if (unvaluedCount === 0) return { type: "complete", Icon: CheckCircle, label: "Valorización Completa" };
	if (valuedCount === 0) return { type: "none", Icon: XCircle, label: "Sin Valorización" };
	return { type: "incomplete", Icon: CircleAlert, label: "Valorización Incompleta" };
};

const OAValorizationCell = ({ details = [], stateId = 0, rowIndex = 0, totalRows = 0 }) => {
	const { computedTotal, unvaluedCount, valuedCount, productRows, totalProducts } = computeValorizationSummary(details, Number(stateId));

	const status = resolveValorizationStatus({ totalProducts, unvaluedCount, valuedCount });
	const { Icon } = status;
	const isBottomRow = totalRows > 0 && rowIndex >= Math.floor(totalRows / 2);

	return (
		<div className={styles.cellWrapper}>
			<div className={`${styles.valueBadge} ${styles[`badge_${status.type}`]}`}>
				{valuedCount === 0 && Icon && <Icon size={13} className={styles.badgeIcon} />}
				<span>${formatNumber(computedTotal)}</span>
			</div>

			<div className={`${styles.tooltipPanel} ${isBottomRow ? styles.tooltipPanelTop : ""}`}>
				<div className={`${styles.tooltipHead} ${styles[`head_${status.type}`]}`}>
					{Icon && <Icon size={16} />}
					<strong>{status.label}</strong>
				</div>

				<table className={styles.tooltipTable}>
					<thead>
						<tr>
							<th className={styles.tableHeader}>Producto</th>
							<th className={`${styles.tableHeader} ${styles.tableHeaderNumeric}`}>Precio</th>
							<th className={`${styles.tableHeader} ${styles.tableHeaderNumeric}`}>Cantidad</th>
							<th className={`${styles.tableHeader} ${styles.tableHeaderNumeric}`}>Subtotal</th>
						</tr>
					</thead>
					<tbody>
						{productRows.map((productRow, index) => (
							<tr key={index} className={`${styles.tableRow} ${productRow.isUnvalued ? styles.tableRowUnvalued : ""}`}>
								<td className={styles.tableCell}>{productRow.productName}</td>
								<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
									{productRow.isUnvalued ? <span className={styles.noPrice}>Sin precio</span> : `$${formatNumber(productRow.effectivePrice)}`}
								</td>
								<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>{formatNumber(productRow.quantity)}</td>
								<td className={`${styles.tableCell} ${styles.tableCellNumeric}`}>
									{productRow.isUnvalued ? "-" : `$${formatNumber(productRow.subtotal)}`}
								</td>
							</tr>
						))}
					</tbody>
				</table>

				<div className={styles.tooltipFooter}>
					<span className={styles.footerLabel}>Total estimado</span>
					<span className={styles.footerTotal}>${formatNumber(computedTotal)}</span>
				</div>
			</div>
		</div>
	);
};

export default OAValorizationCell;
